import React, { useState, useEffect, useCallback } from "react";
import "../page/productInvoice.css";
import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import ProductInvoiceHeader from "../component/productInvoiceHeader";
import ProductInvoiceTable from "../component/productInvoiceTable";
import ModalDetailProductInvoice from "../component/modalDetailProductInvoice";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";
import PaginationFooter from "../../../components/footer/paginationFooter";
import { formatDateTransaction } from "../../../helper/formatDate";

const ProductInvoice = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModalDetailProductInvoice, setIsOpenModalDetailProductInvoice] =
    useState(false);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [selectedProductData, setSelectedProductData] = useState({
    product_invoice_id: "",
    product_invoice_supplier_id: "",
    product_invoice_document_number: "",
    product_invoice_date: "",
    product_invoice_due_date: "",
    product_invoice_products: [],
    product_invoice_total_discount: "",
    product_invoice_total_order: "",
    product_invoice_total_tax: "",
    product_invoice_total_netto: "",
    product_invoice_total_netto_tax: "",
  });
  const [listProductData, setListProductData] = useState(null);
  const [isOpenModalDeleteProduct, setIsOpenModalDeleteProduct] =
    useState(false);
  const [idProductToDelete, setIdProductToDelete] = useState("");
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState("");
  const [filterData, setFilterData] = useState(null)

  // =====================
  // Function Definition
  // =====================

  const searchProduct = (filter) => {
    setSearchInvoiceNumber(filter.invoiceNumber);
    setFilterData({
      supplier_id: filter.distributor.id,
      start_date: filter.entryDate,
    })
  };

  const onDetailProduct = (productData) => {
    setSelectedProductData(productData);
    setIsOpenModalDetailProductInvoice(true);
  };

  const onPaymentInvoiceClicked = (invoiceData) => {
    updateInvoicePaymentStatus(invoiceData.product_invoice_id);
  };

  const onEditProduct = (productData) => {
    setSelectedProductData(productData);
    navigate(`/edit-produk-invoice`, {
      state: { productData },
    });
  };

  const onAddNewProduct = () => {
    navigate("/add-produk-invoice");
  };

  const onDeleteProduct = (productData) => {
    setIsOpenModalDeleteProduct(true);
    setIdProductToDelete(productData.product_invoice_id);
  };

  const onDeleteProductConfirmed = () => {
    setIsOpenModalDeleteProduct(false);
    deleteProduct();
  };

  const onCloseModalDeleteProduct = () => {
    setIdProductToDelete("");
    setIsOpenModalDeleteProduct(false);
  };

  const onCloseModal = () => {
    resetSelectedProductDataValue();
    setIsOpenModalDetailProductInvoice(false);
  };

  const resetSelectedProductDataValue = () => {
    setSelectedProductData({
      product_invoice_id: "",
      product_invoice_supplier_id: "",
      product_invoice_document_number: "",
      product_invoice_date: "",
      product_invoice_due_date: "",
      product_invoice_products: [],
      product_invoice_total_discount: "",
      product_invoice_total_order: "",
      product_invoice_total_tax: "",
      product_invoice_total_netto: "",
      product_invoice_total_netto_tax: "",
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Request Method
  // =====================

  const getProductData = useCallback(async () => {
    try {
      setIsLoading(true);
      localStorage.setItem("searchValue", "");

      const params = new URLSearchParams({
        page: page,
      });

      const url = `${Constant.BASE_API_URL}/invoice-produk?${params.toString()}`
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const product = data.product_invoice;
      if (!product) {
        setListProductData([]);
      }
      setListProductData(product);
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setListProductData([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const deleteProduct = async () => {
    try {
      setIsLoading(true);
      const productId = idProductToDelete;
      const response = await fetch(
        Constant.BASE_API_URL + "/invoice-produk/" + productId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdProductToDelete("");
      getProductData();
    }
  };

  const getProductSearchInvoiceData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = new URL(Constant.BASE_API_URL + "/invoice-produk/search");
      url.searchParams.append("keyword", searchInvoiceNumber);
      url.searchParams.append("page", page);

      if (filterData?.supplier_id) {
        url.searchParams.append('supplier_id', filterData.supplier_id)
      }
      if (filterData?.start_date) {
        url.searchParams.append('start_date', formatDateTransaction(filterData.start_date, 'daily'))
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const product = data.product_invoice;
      if (!product) {
        setListProductData(null);
      } else {
        setListProductData(product);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setListProductData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchInvoiceNumber, navigate, filterData]);

  const updateInvoicePaymentStatus = async (invoiceId) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        Constant.BASE_API_URL +
        "/invoice-produk/update-invoice-status/" +
        invoiceId,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getProductData();
    }
  };

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    if (
      searchInvoiceNumber !== "" ||
      filterData?.supplier_id ||
      filterData?.start_date
    ) {
      getProductSearchInvoiceData();
    } else {
      getProductData();
    }
  }, [
    searchInvoiceNumber,
    page,
    getProductData,
    getProductSearchInvoiceData,
    filterData,
  ]);

  // =====================
  // Render Method
  // =====================

  return (
    <div>
      <h1 className="mb-4">Produk Invoice</h1>
      <ProductInvoiceHeader
        onAddProductClicked={onAddNewProduct}
        onSearchInvoiceNumber={searchProduct}
      />
      <ProductInvoiceTable
        page={page}
        product={listProductData}
        onEditProductClicked={onEditProduct}
        onDeleteProductClicked={onDeleteProduct}
        onDetailProductClicked={onDetailProduct}
        onPaymentInvoiceClicked={onPaymentInvoiceClicked}
        isLoading={isLoading}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
      <ModalDetailProductInvoice
        isShowModal={isOpenModalDetailProductInvoice}
        onCloseModal={onCloseModal}
        productObject={selectedProductData.product_invoice_products}
        key={Date.now()}
      />
      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeleteProduct}
        onCloseModal={onCloseModalDeleteProduct}
        onDeleteConfirmed={onDeleteProductConfirmed}
        idToDelete={idProductToDelete}
      />
    </div>
  );
};

export default ProductInvoice;
